/**
 * Save client for form-page autosave (plot beats, character levels, setup).
 * Same wire discipline as screenplay/saveClient.js — one place owns the
 * payload shape, keepalive byte math, revision handling, and error statuses.
 *
 * The endpoints here return the FULL project doc (existing contract), so the
 * rotated optimistic-concurrency rev arrives as `content_rev` on the doc
 * rather than a bare `rev` field.
 */
import { authFetch } from './authFetch';
import { bodyByteSize, KEEPALIVE_BODY_LIMIT_BYTES } from '../screenplay/saveClient';

/**
 * Returns { save, getRev, setRev } for one endpoint.
 *  - save(payload, meta): PUTs payload + base_rev. meta.unload marks a
 *    page-hide/close flush — the only context where keepalive is used (and
 *    only when the body fits the byte quota).
 *  - Identical consecutive payloads are skipped without a network call.
 *  - Optimistic concurrency: sends the last-seen rev; the server 409s a
 *    stale write. Errors carry .status so the autosaver can tell permanent
 *    rejects from transient failures.
 *  - getRev/setRev: completion flows save outside the autosave path (e.g.
 *    completed: true) and must read the current rev before the request and
 *    push the rotated one back after it. setRev must NEVER regress a live
 *    client's rev from a fetch — a GET served before this client's own PUT
 *    landed carries the pre-rotation rev, and adopting it 409-deadlocks
 *    every save after (callers seed only when getRev() is null).
 */
export function createFormSave({ url, initialRev = null, onRev = null }) {
  let rev = initialRev ?? null;
  let lastSavedJson = null;

  const save = async (data, meta = {}) => {
    const dataJson = JSON.stringify(data);
    if (dataJson === lastSavedJson) return; // nothing changed — no traffic
    const body = JSON.stringify({ ...data, base_rev: rev });
    const res = await authFetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body,
      keepalive: Boolean(meta.unload) && bodyByteSize(body) < KEEPALIVE_BODY_LIMIT_BYTES,
    });
    if (!res.ok) {
      const detail = await res.json().catch(() => ({}));
      const err = new Error(detail.detail || `Save failed (${res.status})`);
      err.status = res.status;
      throw err;
    }
    const doc = await res.json().catch(() => ({}));
    if (doc.content_rev) {
      rev = doc.content_rev;
      onRev?.(rev);
    }
    lastSavedJson = dataJson;
  };

  return {
    save,
    getRev: () => rev,
    setRev: (r) => { rev = r ?? null; },
  };
}
